import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/DiscoverySection.css';
import { trackEvent } from '../services/analytics';
import SwipeDiscovery from './SwipeDiscovery';

const discoveryCards = [
  {
    key: 'subjects',
    title: 'Explore by Subject',
    eyebrow: 'DISCOVERY',
    icon: '🧭',
    description: 'Browse the collection through discipline-based subject shelves with icons, quick counts and direct book access.',
    accentClass: 'discovery-card--subject',
    ctaLabel: 'Open Subject Explorer',
    route: '/subjects',
    eventName: 'subject_explorer_opened',
  },
  {
    key: 'insights',
    title: 'Library Insights',
    eyebrow: 'ANALYTICS',
    icon: '📊',
    description: 'See collection trends, visitor activity, new-arrival momentum and the most-used discovery actions in one visual dashboard.',
    accentClass: 'discovery-card--insights',
    ctaLabel: 'Open Insights Dashboard',
    route: '/insights',
    eventName: 'library_insights_opened',
  },
];

function DiscoverySection() {
  const navigate = useNavigate();
  const [rouletteLoading, setRouletteLoading] = useState(false);
  const [rouletteError, setRouletteError] = useState(null);
  const [showSwipeDiscovery, setShowSwipeDiscovery] = useState(false);

  const handleOpen = (card) => {
    trackEvent(card.eventName, { source: 'home-discovery' });
    navigate(card.route);
  };

  const handleSurpriseMe = async () => {
    setRouletteLoading(true);
    setRouletteError(null);

    try {
      const response = await fetch('/api/books/random');
      if (!response.ok) {
        throw new Error('No available titles found');
      }

      const data = await response.json();
      const book = data.book;
      if (!book?.biblio_id) {
        throw new Error('No available titles found');
      }

      trackEvent('random_book_clicked', { source: 'home-discovery', biblioId: book.biblio_id });
      navigate(`/book/${book.biblio_id}`, { state: { from: 'random-roulette' } });
    } catch (err) {
      setRouletteError('Could not find a surprise pick right now. Please try again.');
      console.error('Random book roulette error:', err);
    } finally {
      setRouletteLoading(false);
    }
  };

  return (
    <section className="discovery-section">
      <div className="discovery-section__header">
        <div>
          <p className="discovery-section__eyebrow">Discover More</p>
          <h2>Explore the collection in two focused ways</h2>
        </div>
        <p className="discovery-section__subtext">
          Browse subject shelves when you want depth. Open Library Insights when you want a visual view of how the collection is being used.
        </p>
      </div>

      <div className="discovery-grid">
        {discoveryCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className={`discovery-card ${card.accentClass}`}
            onClick={() => handleOpen(card)}
          >
            <span className="discovery-card__eyebrow">{card.eyebrow}</span>
            <span className="discovery-card__icon" aria-hidden="true">{card.icon}</span>
            <span className="discovery-card__title">{card.title}</span>
            <span className="discovery-card__description">{card.description}</span>
            <span className="discovery-card__cta">{card.ctaLabel}</span>
          </button>
        ))}
      </div>

      <div id="home-discovery-roulette" className="discovery-roulette">
        <button
          type="button"
          className="discovery-roulette__btn"
          onClick={handleSurpriseMe}
          disabled={rouletteLoading}
        >
          <span aria-hidden="true">🎲</span>{' '}
          {rouletteLoading ? 'Picking a surprise...' : 'Surprise me with a random book'}
        </button>
        {rouletteError && <p className="discovery-roulette__error">{rouletteError}</p>}
        <button
          type="button"
          className="discovery-roulette__btn discovery-roulette__btn--swipe"
          onClick={() => setShowSwipeDiscovery(true)}
        >
          <span aria-hidden="true">🔥</span> Swipe to discover
        </button>
      </div>

      {showSwipeDiscovery && (
        <SwipeDiscovery onClose={() => setShowSwipeDiscovery(false)} />
      )}
    </section>
  );
}

export default DiscoverySection;